import { createContext, useState, useEffect, useCallback, useRef, type ReactNode } from 'react';
import { User } from 'oidc-client-ts';
import { userManager } from './userManager';
import type { AuthContextValue } from '@/services/auth/types';
import { clearTodaysDailyRoSummaryAcks } from '@/lib/dailyRoSummaryStorage';
import {
    consumeSkipNextChatSessionHydrate,
    markSkipNextChatSessionHydrate,
} from '@/services/auth/chatSessionHydrate';
import {
    isExtensionAuth,
    setRedirectInExtensionStorage,
    startExtensionAuth,
    getStoredUserFromExtensionStorage,
    addExtensionStorageListener,
    clearUserFromExtensionStorage,
} from './extensionAuth';

const POST_LOGIN_KEY = 'wc.auth.post_login_redirect';

// eslint-disable-next-line react-refresh/only-export-components
export const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
    const [user, setUser] = useState<User | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [chatSessionEpoch, setChatSessionEpoch] = useState(0);
    const authSettledOnce = useRef(false);
    const hadAuthenticatedUser = useRef(false);

    useEffect(() => {
        const loadUser = async () => {
            try {
                if (isExtensionAuth()) {
                    const storedStr = await getStoredUserFromExtensionStorage();
                    if (storedStr) {
                        try {
                            const parsed = User.fromStorageString(storedStr);
                            if (parsed && !parsed.expired) {
                                await userManager.storeUser(parsed);
                                setUser(parsed);
                            }
                        } catch {
                            await clearUserFromExtensionStorage();
                        }
                    }
                }
                const stored = await userManager.getUser();
                setUser(prev => (stored && !stored.expired ? stored : (prev ?? null)));
            } catch (err) {
                console.error('Error loading user:', err);
                setUser(null);
            } finally {
                setIsLoading(false);
            }
        };

        void loadUser();

        const onLoaded = (u: User) => setUser(u);
        const onUnloaded = () => setUser(null);
        const onRenewError = (err: Error) => console.error('Silent renew error:', err);

        userManager.events.addUserLoaded(onLoaded);
        userManager.events.addUserUnloaded(onUnloaded);
        userManager.events.addSilentRenewError(onRenewError);

        const removeStorageListener =
            isExtensionAuth() &&
            addExtensionStorageListener(async userStr => {
                if (userStr) {
                    try {
                        const parsed = User.fromStorageString(userStr);
                        if (parsed && !parsed.expired) {
                            await userManager.storeUser(parsed);
                            setUser(parsed);
                        } else {
                            await userManager.removeUser();
                            setUser(null);
                        }
                    } catch {
                        await userManager.removeUser();
                        setUser(null);
                    }
                } else {
                    await userManager.removeUser();
                    setUser(null);
                }
            });

        return () => {
            userManager.events.removeUserLoaded(onLoaded);
            userManager.events.removeUserUnloaded(onUnloaded);
            userManager.events.removeSilentRenewError(onRenewError);
            if (typeof removeStorageListener === 'function') removeStorageListener();
        };
    }, []);

    const isAuthenticated = !!user && !user.expired;

    useEffect(() => {
        if (isLoading) return;
        if (!authSettledOnce.current) {
            authSettledOnce.current = true;
            hadAuthenticatedUser.current = isAuthenticated;
            return;
        }
        const wasAuthenticated = hadAuthenticatedUser.current;
        if (wasAuthenticated && !isAuthenticated) {
            markSkipNextChatSessionHydrate();
        }
        if (isAuthenticated && !wasAuthenticated) {
            clearTodaysDailyRoSummaryAcks();
            setChatSessionEpoch(e => e + 1);
        }
        hadAuthenticatedUser.current = isAuthenticated;
    }, [isLoading, isAuthenticated]);

    const consumeChatSessionRestoreSkip = useCallback((): boolean => {
        return consumeSkipNextChatSessionHydrate();
    }, []);

    const login = useCallback(async (redirectPath?: string) => {
        if (isExtensionAuth()) {
            await setRedirectInExtensionStorage(redirectPath ?? '/');
            const result = await startExtensionAuth(redirectPath ?? '/');
            if (!result.ok) {
                throw new Error(result.error || 'Login failed');
            }
            return;
        }
        if (redirectPath) {
            sessionStorage.setItem(POST_LOGIN_KEY, redirectPath);
        }
        await userManager.signinRedirect();
    }, []);

    const logout = async () => {
        markSkipNextChatSessionHydrate();
        if (isExtensionAuth()) {
            await clearUserFromExtensionStorage();
            await userManager.removeUser();
            setUser(null);
            // Navigate in-app to login so user sees extension login page (avoids Cognito
            // redirect to chrome-extension:// which triggers ERR_BLOCKED_BY_CLIENT).
            window.location.hash = '#/login';
            return;
        }
        await userManager.signoutRedirect();
    };

    const getAccessToken = async (): Promise<string | null> => {
        const u = await userManager.getUser();
        return u?.access_token ?? null;
    };

    return (
        <AuthContext.Provider
            value={{
                user,
                isLoading,
                isAuthenticated,
                chatSessionEpoch,
                consumeChatSessionRestoreSkip,
                login,
                logout,
                getAccessToken,
            }}
        >
            {children}
        </AuthContext.Provider>
    );
}
